import { useContext, useEffect, useReducer, useRef, useState } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
} from '@ea-lab/reactive-json';

// ── Canaux de sauvegarde (registre module-level, hors contexte reactive-json) ──
// Une fiche peut être composée de PLUSIEURS racines reactive-json disjointes
// (ex. la fiche tâche : en-tête persistant + onglet Informations), chacune avec
// sa propre DataSync et son propre Syncable Object. Le store RJ ne franchit pas
// la frontière d'une racine : ce registre permet à UN SEUL bouton visible de
// déclencher — et de refléter l'état de — toutes les DataSync d'un même
// `saveChannel`. Les membres publient une ref (état + fonction de save) ; les
// abonnés (le bouton visible) sont re-rendus quand un membre change d'état.
const saveChannels = new Map();

const channelOf = (name) => {
    let channel = saveChannels.get(name);
    if (!channel) {
        channel = { members: new Set(), listeners: new Set() };
        saveChannels.set(name, channel);
    }
    return channel;
};

const notifyChannel = (name) => {
    const channel = saveChannels.get(name);
    if (channel) channel.listeners.forEach((fn) => fn());
};

const joinChannel = (name, memberRef) => {
    const channel = channelOf(name);
    channel.members.add(memberRef);
    notifyChannel(name);
    return () => {
        channel.members.delete(memberRef);
        notifyChannel(name);
    };
};

const subscribeToChannel = (name, fn) => {
    const channel = channelOf(name);
    channel.listeners.add(fn);
    return () => {
        channel.listeners.delete(fn);
    };
};

// État agrégé du canal : « sale » dès qu'UN membre l'est, et statut le plus
// « fort » (saving > error > success) pour que le bouton unique reste lisible.
const aggregateChannel = (name) => {
    const channel = saveChannels.get(name);
    const members = channel ? [...channel.members] : [];
    let hasChanges = false;
    let displayStatus = null;
    for (const member of members) {
        const state = member.current;
        if (!state) continue;
        if (state.hasChanges) hasChanges = true;
        if (state.displayStatus === 'saving') displayStatus = 'saving';
        else if (state.displayStatus === 'error' && displayStatus !== 'saving') displayStatus = 'error';
        else if (state.displayStatus === 'success' && displayStatus === null) displayStatus = 'success';
    }
    return {
        hasChanges,
        displayStatus,
        save: () => members.forEach((member) => member.current?.doSave?.()),
    };
};

/**
 * Rendu visuel partagé du bouton (aucune logique DataSync) : cinq états.
 *
 *   pristine → gris/désactivé  "Sauvegarder" + infobulle "Aucune modification…"
 *   idle     → violet          "Sauvegarder"
 *   saving   → gris/désactivé  "Sauvegarde..." + spinner
 *   success  → vert            "Sauvegardé"  + coche
 *   error    → rouge           "Erreur"      + croix
 */
const SaveButtonView = ({ hasChanges, displayStatus, onClick, className }) => {
    const isSaving = displayStatus === 'saving';
    const isSuccess = displayStatus === 'success';
    const isError = displayStatus === 'error';
    const isPristine = !hasChanges && !isSaving && !isSuccess && !isError;

    const btnClass = [
        'btn inline-flex items-center gap-2',
        isSaving || isPristine
            ? 'btn-secondary cursor-not-allowed'
            : isSuccess
            ? 'btn-success'
            : isError
            ? 'btn-danger'
            : 'btn-primary',
    ].join(' ');

    return (
        <div className={`relative group/savebtn inline-block ${className ?? ''}`}>
            <button
                type="button"
                onClick={onClick}
                disabled={isSaving || isPristine}
                className={btnClass}
            >
                {isSaving ? (
                    <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sauvegarde...
                    </>
                ) : isSuccess ? (
                    <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Sauvegardé
                    </>
                ) : isError ? (
                    <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                        Erreur
                    </>
                ) : (
                    'Sauvegarder'
                )}
            </button>

            {isPristine && (
                <div className="absolute bottom-full right-0 mb-1.5 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg shadow-lg whitespace-nowrap invisible group-hover/savebtn:visible pointer-events-none z-50 transition-opacity">
                    Aucune modification à sauvegarder
                    <div className="absolute top-full right-4 -mt-px border-4 border-transparent border-t-slate-800" />
                </div>
            )}
        </div>
    );
};

/**
 * DataSyncSaveButton — a reactive-json element component that renders a
 * DataSync-aware save button (see SaveButtonView for the five visual states).
 *
 * Dirty-tracking: on first render the component snapshots syncable.data as the
 * baseline. Any subsequent change to syncable.data marks the form as dirty.
 * After a successful save the baseline is updated to the newly saved data.
 *
 * YAML usage:
 *   - type: DataSyncSaveButton
 *     triggerLocation: ~~.saveNow        # boolean path that triggers DataSync
 *     syncablePath: ~~.taskSyncable      # the Syncable Object watched by DataSync
 *     saveChannel: "task"                # optionnel : rejoint un canal partagé
 *     headless: true                     # optionnel : ne rend rien (le bouton
 *                                        #   visible du canal pilote ce membre)
 */
export const DataSyncSaveButton = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    // Read the full Syncable Object to access its data and status
    const syncable = evaluateTemplateValue({
        valueToEvaluate: props.syncablePath,
        globalDataContext,
        templateContext,
    });
    const statusType = syncable?.status?.type ?? null;

    // Resolve the trigger path so we can write `true` on click
    const { formDataPath: triggerPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.triggerLocation,
        defaultValue: false,
        globalDataContext,
        templateContext,
    });

    // ── Dirty tracking ────────────────────────────────────────────────────────
    // Snapshot syncable.data on first render with actual content, then compare
    // on every render to know whether there is anything worth saving.
    const baselineRef = useRef(null);
    const currentDataStr = JSON.stringify(syncable?.data ?? null);

    if (
        baselineRef.current === null &&
        syncable?.data &&
        Object.keys(syncable.data).length > 0
    ) {
        baselineRef.current = currentDataStr;
    }

    const hasChanges =
        baselineRef.current !== null && currentDataStr !== baselineRef.current;

    // ── Display state ─────────────────────────────────────────────────────────
    const [displayStatus, setDisplayStatus] = useState(null);
    const prevTypeRef = useRef(null);

    useEffect(() => {
        if (statusType === prevTypeRef.current) return;
        prevTypeRef.current = statusType;

        if (statusType === 'info') {
            setDisplayStatus('saving');
            return;
        }
        if (statusType === 'success') {
            // Reset baseline so the button goes back to pristine after save
            baselineRef.current = currentDataStr;
            setDisplayStatus('success');
            // Notify parent components (across ReactiveJsonRoot boundary) that
            // the entity was saved so they can refresh history / comments.
            window.dispatchEvent(new CustomEvent('task-saved'));
            const t = setTimeout(() => setDisplayStatus(null), 2000);
            return () => clearTimeout(t);
        }
        if (statusType === 'error') {
            setDisplayStatus('error');
            const t = setTimeout(() => setDisplayStatus(null), 5000);
            return () => clearTimeout(t);
        }
        setDisplayStatus(null);
    }, [statusType]);

    const { formDataPath: syncableBasePath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.syncablePath,
        defaultValue: {},
        globalDataContext,
        templateContext,
    });

    const doSave = () => {
        if (!hasChanges || displayStatus === 'saving') return;

        // Drupal decoupled_toolbox rejects "" for entity-reference / date fields.
        // Convert empty strings to null before the DataSync POST fires.
        if (syncable?.data && syncableBasePath) {
            for (const [key, val] of Object.entries(syncable.data)) {
                if (val === '' && typeof val === 'string') {
                    globalDataContext.updateData(null, `${syncableBasePath}.data.${key}`);
                }
            }
        }

        globalDataContext.updateData(true, triggerPath);
    };

    // ── Participation au canal partagé ────────────────────────────────────────
    // La ref est stable (identité de membre) et rafraîchie à chaque rendu ; on ne
    // réveille les abonnés que lorsque l'état visible change réellement.
    const channel = props.saveChannel ?? null;
    const memberRef = useRef(null);
    memberRef.current = { hasChanges, displayStatus, doSave };
    const signatureRef = useRef(null);

    useEffect(() => {
        if (!channel) return;
        return joinChannel(channel, memberRef);
    }, [channel]);

    useEffect(() => {
        if (!channel) return;
        const signature = `${hasChanges}|${displayStatus}`;
        if (signatureRef.current === signature) return;
        signatureRef.current = signature;
        notifyChannel(channel);
    });

    if (props.headless) return null;

    return (
        <ActionDependant {...props}>
            <SaveButtonView
                hasChanges={hasChanges}
                displayStatus={displayStatus}
                onClick={doSave}
            />
        </ActionDependant>
    );
};

/**
 * SaveChannelButton — bouton de sauvegarde UNIQUE pour un `saveChannel`, utilisable
 * en React classique (hors racine reactive-json). Il agrège l'état de toutes les
 * DataSyncSaveButton `headless` du canal et déclenche leurs sauvegardes.
 */
export const SaveChannelButton = ({ channel, className }) => {
    const [, forceRender] = useReducer((x) => x + 1, 0);

    useEffect(() => subscribeToChannel(channel, forceRender), [channel]);

    const { hasChanges, displayStatus, save } = aggregateChannel(channel);

    return (
        <SaveButtonView
            hasChanges={hasChanges}
            displayStatus={displayStatus}
            onClick={save}
            className={className}
        />
    );
};
